import { Resource, Route } from 'model';
import Fuse from 'fuse.js';

/**
 * Searches resources using Fuse.js fuzzy matching
 * @param resources - Array of resources to search through
 * @param searchText - The search term
 * @param fuzzyThreshold - Similarity threshold for fuzzy matching (0-1, default: 0.4)
 * @returns Filtered array of resources that match the search criteria
 */
export function searchResources(resources: Resource[], searchText: string, fuzzyThreshold: number = 0.4): Resource[] {
  if (!searchText || searchText.trim() === '') {
    return resources;
  }

  // Configure Fuse.js options
  const fuseOptions = {
    // Fields to search in
    keys: [
      {
        name: 'title',
        weight: 0.4 // Higher weight for title matches
      },
      {
        name: 'description',
        weight: 0.3
      },
      {
        name: 'tags',
        weight: 0.3
      }
    ],
    // Include score in results for debugging/sorting
    includeScore: true,
    // Threshold for fuzzy matching (0.0 = perfect match, 1.0 = match anything)
    threshold: fuzzyThreshold,
    // Ignore location when computing score
    ignoreLocation: true,
    // Minimum character length for fuzzy matching
    minMatchCharLength: 1,
    // Whether to sort results by score
    shouldSort: true,
    // Use extended search for more advanced queries
    useExtendedSearch: false
  };

  // Create Fuse instance
  const fuse = new Fuse(resources, fuseOptions);

  // Perform search and extract items from results
  const results = fuse.search(searchText.trim());

  // Return just the items (without scores)
  return results.map((result) => result.item);
}

/**
 * Searches routes using Fuse.js fuzzy matching
 * @param routes - Array of routes to search through
 * @param searchText - The search term
 * @param fuzzyThreshold - Similarity threshold for fuzzy matching (0-1, default: 0.4)
 * @returns Filtered array of routes that match the search criteria
 */
export function searchRoutes(routes: Route[], searchText: string, fuzzyThreshold: number = 0.4): Route[] {
  if (!searchText || searchText.trim() === '') {
    return routes;
  }

  const fuseOptions = {
    keys: [
      {
        name: 'name',
        weight: 0.4
      },
      {
        name: 'location',
        weight: 0.3
      },
      {
        name: 'description',
        weight: 0.2
      },
      {
        name: 'tags',
        weight: 0.1
      }
    ],
    includeScore: true,
    threshold: fuzzyThreshold,
    ignoreLocation: true,
    minMatchCharLength: 1,
    shouldSort: true,
    useExtendedSearch: false
  };

  const fuse = new Fuse(routes, fuseOptions);
  const results = fuse.search(searchText.trim());

  return results.map((result) => result.item);
}
